/**
 * Phase 7C-4 -- the only two things the Website persists about a session.
 *
 *   sessionStorage  jinda_pos_session_token   the bearer token
 *   localStorage    jinda_pos_web_device_id   a random, non-secret device id
 *
 * WHY sessionStorage FOR THE TOKEN
 * ----------------------------------------------------------------------
 * `sessionStorage` is scoped to one tab and is discarded when that tab or
 * the browser is closed, so a till left logged in overnight does not stay
 * logged in on disk. A refresh of the same tab keeps it, which is what
 * lets `App.tsx` revalidate with `SESSION_ME` instead of asking for the
 * password again. Nothing about this is a security boundary on its own:
 * the Backend expiry (`absolute_expires_at`) is the real bound, and the
 * gate ALWAYS asks the Backend before trusting the stored value.
 *
 * Two browser behaviours worth knowing (documented, not worked around):
 * "Reopen closed tab" and "continue where you left off" can restore a
 * tab's `sessionStorage`, and "Duplicate tab" copies it into the new
 * tab. In both cases the token is still validated by `SESSION_ME` and
 * still expires on the Backend's clock.
 *
 * A persistent "remember me" token in `localStorage` is deliberately not
 * offered. A future persistent web login needs a Backend-issued HttpOnly
 * cookie, which is outside this phase.
 *
 * WHY THE DEVICE ID IS RANDOM
 * ----------------------------------------------------------------------
 * The Backend uses `device_id` to tell "same till signing in again"
 * (rotate the session) from "another device" (409 conflict). It only has
 * to be stable and unique per browser profile, so it is 16 random bytes
 * from `crypto.getRandomValues()` -- NOT anything derived from the
 * browser, the hardware, the employee or the network. Fingerprinting
 * would make two identical tills collide, and colliding ids make the
 * Backend silently revoke a colleague's session on every login.
 *
 * It is stored in `localStorage` because it is an identifier, not a
 * credential: knowing it grants nothing.
 *
 * NOTHING HERE LOGS A TOKEN.
 */

export const SESSION_TOKEN_KEY = 'jinda_pos_session_token';
export const DEVICE_ID_KEY = 'jinda_pos_web_device_id';

/**
 * The legacy Phase-7B-era authentication keys, and only those:
 *   `uuid` -- the whole legacy LOGIN response, base64-encoded, which
 *             `App.tsx` used to accept as proof of being logged in;
 *   `p`    -- the privilege, base64-encoded; written by three places and
 *             read by none.
 * Removed on every startup. Preferences such as `theme`, `pause`, the
 * print settings and the cached business data are NOT auth state and are
 * left alone.
 */
export const LEGACY_AUTH_KEYS: ReadonlyArray<string> = ['uuid', 'p'];

/** Backend rule, copied from `handler/auth/session/sessionService.py`. */
const DEVICE_ID_PATTERN = /^[A-Za-z0-9._:-]{8,64}$/;
const DEVICE_ID_PREFIX = 'web-';
const DEVICE_ID_BYTES = 16;
/** Backend caps `device_label` at 60 characters. */
const DEVICE_LABEL_MAX = 60;

type StorageKind = 'session' | 'local';

/**
 * Storage access can throw (Safari private mode, a disabled-storage
 * policy, an `about:blank` frame), so every read and write goes through a
 * guard and degrades to "nothing stored" instead of breaking the page.
 */
function storageOf(kind: StorageKind): Storage | null {
    try {
        if (typeof window === 'undefined') return null;
        return kind === 'session' ? window.sessionStorage : window.localStorage;
    } catch (e) {
        return null;
    }
}

export const readSessionToken = (): string | null => {
    try {
        const value = storageOf('session')?.getItem(SESSION_TOKEN_KEY);
        return value && value.length > 0 ? value : null;
    } catch (e) {
        return null;
    }
};

export const writeSessionToken = (token: string): void => {
    try {
        storageOf('session')?.setItem(SESSION_TOKEN_KEY, token);
    } catch (e) {
        // Nothing to do: the in-memory session still works for this page
        // load, and a refresh simply asks for the password again.
    }
};

export const clearSessionToken = (): void => {
    try {
        storageOf('session')?.removeItem(SESSION_TOKEN_KEY);
    } catch (e) {
        // Ignored -- see writeSessionToken.
    }
};

export const isValidDeviceId = (value: unknown): value is string => {
    return typeof value === 'string' && DEVICE_ID_PATTERN.test(value);
};

function randomHex(bytes: number): string {
    const cryptoApi: Crypto | undefined = typeof globalThis !== 'undefined'
        ? (globalThis as { crypto?: Crypto }).crypto
        : undefined;
    if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') {
        // Every browser in `browserslist` has Web Crypto. Failing loudly
        // beats a Math.random() identifier that only looks unique.
        throw new Error('เบราว์เซอร์นี้ไม่รองรับการสร้างรหัสอุปกรณ์อย่างปลอดภัย');
    }
    const buffer = new Uint8Array(bytes);
    cryptoApi.getRandomValues(buffer);
    let hex = '';
    for (let i = 0; i < buffer.length; i++) {
        hex += buffer[i].toString(16).padStart(2, '0');
    }
    return hex;
}

export const generateDeviceId = (): string => DEVICE_ID_PREFIX + randomHex(DEVICE_ID_BYTES);

/**
 * The persisted device id, creating and storing one on first use.
 *
 * A stored value that no longer satisfies the Backend pattern (an older
 * build, a corrupted entry) is replaced rather than sent and rejected
 * with a 400.
 */
export const getOrCreateDeviceId = (): string => {
    const local = storageOf('local');
    let existing: string | null = null;
    try {
        existing = local?.getItem(DEVICE_ID_KEY) ?? null;
    } catch (e) {
        existing = null;
    }
    if (isValidDeviceId(existing)) return existing;

    const generated = generateDeviceId();
    try {
        local?.setItem(DEVICE_ID_KEY, generated);
    } catch (e) {
        // Storage unavailable: the id is used for this page load only and a
        // new one is generated next time. The Backend then sees a "new
        // device", which is the safe direction to fail in.
    }
    return generated;
};

/**
 * A coarse browser family for the 409 message ("Chrome" / "Firefox" /
 * ...). This is a display label, not an identifier: nothing about the
 * device id depends on it, and it is never compared to anything.
 */
export const browserFamily = (userAgent?: string): string => {
    const ua = (userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')) || '';
    if (/Edg\//.test(ua)) return 'Edge';
    if (/OPR\//.test(ua)) return 'Opera';
    if (/Chrome\//.test(ua)) return 'Chrome';
    if (/Firefox\//.test(ua)) return 'Firefox';
    if (/Safari\//.test(ua)) return 'Safari';
    return 'Browser';
};

/**
 * Human-readable name shown to the *other* user when they hit a 409 and
 * have to decide whether to sign this device out. Same shape as Mobile's
 * `Jinda POS Mobile (xxxxxx)`, so the two clients read alike in that
 * dialog. Contains no employee data and no secret.
 */
export const buildDeviceLabel = (deviceId: string, userAgent?: string): string => {
    const suffix = deviceId.length <= 6 ? deviceId : deviceId.slice(-6);
    return `Jinda POS Web ${browserFamily(userAgent)} (${suffix})`.slice(0, DEVICE_LABEL_MAX);
};

/** Removes the legacy auth keys and nothing else. Idempotent. */
export const clearLegacyAuthState = (): void => {
    const local = storageOf('local');
    if (!local) return;
    LEGACY_AUTH_KEYS.forEach((key) => {
        try {
            local.removeItem(key);
        } catch (e) {
            // Ignored -- a key that cannot be removed cannot be read either.
        }
    });
};
